import { memo, useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'

import { Quote, QuoteStatus } from '../../storage/types'
import { ItemValue } from '../../../../components/styled'
import TestIds from '../../../../testIds'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import { styled } from '@mui/material'

const getExpiryText = (expiresOn: Dayjs) => {
  const diff = expiresOn.diff(undefined, 'seconds')
  const diffDays = Math.floor(diff / (60 * 60 * 24))
  const diffHours = Math.floor((diff % (60 * 60 * 24)) / (60 * 60))
  const diffMins = Math.floor((diff % (60 * 60)) / 60)
  const diffSeconds = diff % 60

  if (diffDays > 0) {
    return `Expires in ${diffDays} day${diffDays > 1 ? 's' : ''}`
  } else if (diffHours > 0) {
    return `Expires in ${diffHours} hour${diffHours > 1 ? 's' : ''}`
  } else if (diffMins > 0) {
    return `Expires in ${diffMins} minute${diffMins > 1 ? 's' : ''}`
  } else if (diffSeconds > 0) {
    return `Expires in ${diffSeconds} second${diffSeconds > 1 ? 's' : ''}`
  } else {
    return 'Expired'
  }
}

const IconExpiryContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const Expiry = ({ quote }: { quote: Quote }) => {
  const expiresOn = dayjs(quote.expiresOn)

  const [expiryText, setExpiryText] = useState(getExpiryText(expiresOn))

  useEffect(() => {
    const intervalId = setInterval(() => {
      const expiry = getExpiryText(expiresOn)
      setExpiryText(expiry)
    }, 1000) // Update every second

    return () => clearInterval(intervalId) // Clean up on unmount
  }, [expiresOn])
  if (
    [
      QuoteStatus.Created,
      QuoteStatus.Requested,
      QuoteStatus.Rejected,
      QuoteStatus.Cancelled,
      QuoteStatus.Ordered,
    ].includes(quote.status)
  ) {
    return null
  }
  if (quote.status === QuoteStatus.Expired || expiresOn.isBefore()) {
    return (
      <ItemValue variant='body2' data-testid={TestIds.feat.quotes.card.expiryDate}>
        Expired {expiresOn.format('DD MMM YYYY')}
      </ItemValue>
    )
  }

  if (!quote.expiresOn) return null
  if (expiresOn.isAfter()) {
    const dateString = dayjs().to(expiresOn)

    const diff = expiresOn.diff(undefined, 'day')

    if (diff <= 7)
      return (
        <IconExpiryContainer>
          <MaterialSymbol
            sx={{
              color: '#AC622A',
              fontWeight: 700,
            }}
          >
            Alarm
          </MaterialSymbol>
          <ItemValue
            color='secondary700'
            variant='body2'
            fontWeight={700}
            data-testid={TestIds.feat.quotes.card.expiryDate}
          >
            {expiryText}
          </ItemValue>
        </IconExpiryContainer>
      )
    return (
      <ItemValue variant='body2' data-testid={TestIds.feat.quotes.card.expiryDate}>
        Expires {dateString}
      </ItemValue>
    )
  }
}

export const QuoteExpiry = memo(Expiry, (quote1, quote2) => {
  return quote1.quote.expiresOn === quote2.quote.expiresOn
})
